class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
    this.isMergingDuplicates = false;
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  async fetchCart() {
    const response = await fetch('/cart.js');
    return response.json();
  }

  normalizeBundleComponents(value) {
    if (value == null) return '';
    if (typeof value === 'string') return value.trim();
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }

  getLineMergeKey(item) {
    const variantId = item && (item.id || item.variant_id);
    if (!variantId) return null;

    const props = item && item.properties;
    const bundleComponents = props && (props['_bundle_components'] ?? props._bundle_components);

    // Bundles can legitimately share the same variantId but represent different configurations
    // (e.g. different child components).
    if (bundleComponents == null || bundleComponents === '') return String(variantId);

    return `${variantId}::bundle::${this.normalizeBundleComponents(bundleComponents)}`;
  }

  findDuplicateLinesByVariantId(cartItems = []) {
    const byKey = new Map();

    cartItems.forEach((item, index) => {
      const key = this.getLineMergeKey(item);
      if (!key) return;

      const indices = byKey.get(key) || [];
      indices.push(index);
      byKey.set(key, indices);
    });

    return Array.from(byKey.entries())
      .filter(([, indices]) => indices.length > 1)
      .map(([key, indices]) => ({ key, indices }));
  }

  buildUpdatesForDuplicateMerge(items = [], duplicateGroups = []) {
    const updates = items.map((item) => item.quantity);

    duplicateGroups.forEach((group) => {
      const primaryIndex = group.indices[0];
      const totalQuantity = group.indices.reduce((sum, idx) => sum + (items[idx] ? items[idx].quantity : 0), 0);

      updates[primaryIndex] = totalQuantity;
      group.indices.slice(1).forEach((idx) => {
        updates[idx] = 0;
      });
    });

    return updates;
  }

  getSectionsToRenderForDuplicateMerge() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      return ['cart-drawer', 'cart-icon-bubble', 'cart-live-region-text'];
    }

    return this.getSectionsToRender().map((section) => section.section);
  }

  renderMergedCartSections(parsedState) {
    if (!parsedState || !parsedState.sections) return false;

    if (this.tagName === 'CART-DRAWER-ITEMS') {
      const sectionHtml = parsedState.sections['cart-drawer'];
      if (sectionHtml) {
        const parsedHTML = new DOMParser().parseFromString(sectionHtml, 'text/html');

        ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
          const targetElement = document.querySelector(selector);
          const sourceElement = parsedHTML.querySelector(selector);
          if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
        });
      }

      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && parsedState.sections['cart-icon-bubble']) {
        bubble.innerHTML = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble'], '.shopify-section');
      }

      const liveRegion = document.getElementById('cart-live-region-text');
      if (liveRegion && parsedState.sections['cart-live-region-text']) {
        liveRegion.innerHTML = this.getSectionInnerHTML(
          parsedState.sections['cart-live-region-text'],
          '.shopify-section',
        );
      }

      return true;
    }

    const sectionsToRender = this.getSectionsToRender();
    sectionsToRender.forEach((section) => {
      const html = parsedState.sections[section.section];
      if (!html) return;

      const element = document.getElementById(section.id);
      const elementToReplace = element && element.querySelector(section.selector);
      if (elementToReplace) elementToReplace.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    this.classList.toggle('is-empty', parsedState.item_count === 0);

    const cartDrawerWrapper = document.querySelector('cart-drawer');
    const cartFooter = document.getElementById('main-cart-footer');
    if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
    if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

    return true;
  }

  async applyCartUpdates(updates, options = {}) {
    const cartUpdateUrl =
      typeof routes !== 'undefined' && routes.cart_update_url ? routes.cart_update_url : '/cart/update.js';

    const payload = { updates };

    if (options.sections && options.sections.length) {
      payload.sections = options.sections;
      payload.sections_url =
        options.sectionsUrl ||
        (typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : window.location.pathname);
    }

    const body = JSON.stringify(payload);
    const response = await fetch(cartUpdateUrl, { ...fetchConfig(), ...{ body } });
    return response.json();
  }

  async mergeDuplicateLinesByVariantId(cartOrParsedState) {
    if (this.isMergingDuplicates || window.__cartMergeDuplicatesInProgress) return false;

    let items = cartOrParsedState && Array.isArray(cartOrParsedState.items) ? cartOrParsedState.items : null;

    if (!items) {
      const cart = await this.fetchCart();
      items = cart && Array.isArray(cart.items) ? cart.items : [];
    }

    const duplicateGroups = this.findDuplicateLinesByVariantId(items);

    if (!duplicateGroups.length) return false;

    const updates = this.buildUpdatesForDuplicateMerge(items, duplicateGroups);

    this.isMergingDuplicates = true;
    window.__cartMergeDuplicatesInProgress = true;

    try {
      const sections = this.getSectionsToRenderForDuplicateMerge();
      const sectionsUrl = typeof routes !== 'undefined' && routes.cart_url ? routes.cart_url : window.location.pathname;

      const mergedCart = await this.applyCartUpdates(updates, { sections, sectionsUrl });

      // Let cart listeners refresh after the merge.
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: mergedCart, mergeDuplicates: true });

      //Fallback to the existing refresh flow.
      const didRender = this.renderMergedCartSections(mergedCart);
      if (!didRender) this.onCartUpdate();

      return true;
    } catch (error) {
      console.error('Error merging duplicate cart lines:', error);
      return false;
    } finally {
      this.isMergingDuplicates = false;
      window.__cartMergeDuplicatesInProgress = false;
    }
  }

  async checkCartItems() {
    try {
      const cart = await fetch('/cart.js').then((response) => response.json());

      const variantPromises = cart.items.map((item) =>
        fetch(`/variants/${item.id}.js`).then((response) => response.json()),
      );

      const variants = await Promise.all(variantPromises);
      for (let i = 0; i < variants.length; i++) {
        if (!variants[i].available) {
          await this.removeFromCart(cart.items[i].id);
        }
      }
    } catch (error) {
      console.error('Error checking cart items:', error);
    }
  }

  async removeFromCart(itemId) {
    try {
      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates: { [itemId]: 0 } }),
      });

      this.getSectionsToRender().forEach((section) => {
        const elementToReplace =
          document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
        elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
      });
      return response.json();
    } catch (error) {
      console.error('Error removing item from cart:', error);
    }
  }

  connectedCallback() {
    this.checkCartItems();

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (this.isMergingDuplicates) return;

      // Ignore regular updates initiated by this component (it already re-rendered sections).
      // For merge events, allow other instances to refresh.
      if (event.source === 'cart-items' && !event.mergeDuplicates) {
        return;
      }

      this.mergeDuplicateLinesByVariantId(event.cartData)
        .then((didMerge) => {
          if (!didMerge) this.onCartUpdate();
        })
        .catch(() => {
          this.onCartUpdate();
        });
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        const state = response.text();
        return Promise.all([state, response.status]);
      })
      .then(([state, status]) => {
        const parsedState = JSON.parse(state);
        const errorMessage =
          status === 422 ? `${parsedState.errors} Please clear your cart and try again.` : parsedState.errors;
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (status === 422 || parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, errorMessage);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const mainCartWrapper = document.getElementById('main-cart-items');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const announcement = document.getElementById('cart-announcement');
        if (announcement) {
          announcement.textContent = 'cart updated';
          announcement.focus();
          // clear text so NVDA doesn't reuse it
          setTimeout(() => {
            announcement.textContent = '';
          }, 1500);
        }
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        setTimeout(() => {
          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);

          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && mainCartWrapper) {
            mainCartWrapper.querySelector('.cart-item__name').focus();
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        }, 2000);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });

        const duplicateGroups = this.findDuplicateLinesByVariantId(parsedState.items);
        if (duplicateGroups.length) {
          setTimeout(() => {
            this.mergeDuplicateLinesByVariantId(parsedState);
          }, 0);
        }
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER),
        );
      }
    },
  );
}
